// View body prefix stripper (Slice 18 helper, factored out in Slice 19 for
// symmetry with `inlineTvfPrefix.ts`).
//
// Strips `CREATE [OR REPLACE] [MATERIALIZED] VIEW <ident…> AS` so the body
// parses as a SELECT, AND replaces PG/MySQL-style `FROM (...)` wrapping
// parens with spaces so the inner JOIN list is reachable by the structural
// extractor. Returns the trimmed source plus the byte offset to add back to
// ranges so they remain correct against the original body. The space-for-
// paren swap preserves byte length, so emitted ranges still align with the
// ORIGINAL `bodyText`.

export function stripCreateViewPrefix(body: string): { selectSource: string; byteOffset: number } {
  // `CREATE [OR REPLACE]` (PG/MySQL/Oracle) + `CREATE [OR ALTER]` (MSSQL
  // proprietary) + `ALTER VIEW` (PG/MSSQL alter shape; the body fetcher may
  // return the original DDL text after an ALTER lands).
  const m = /^\s*(?:create\s+(?:or\s+(?:replace|alter)\s+)?(?:materialized\s+)?view|alter\s+view)\s+[\s\S]+?\bas\b\s*/i.exec(body);
  const byteOffset = m ? m[0].length : 0;
  const tail = m ? body.slice(byteOffset) : body;
  return { selectSource: unwrapJoinListParens(tail), byteOffset };
}

/** PG (`pg_get_viewdef`) and MySQL (`information_schema.VIEWS.VIEW_DEFINITION`)
 *  both wrap the FROM-clause's JOIN list in parentheses (`FROM (a JOIN b ON …)`).
 *  The grammar's FROM extractor treats those parens as a derived-table; the
 *  inner content (which is NOT a SELECT) then yields no fromItems / no resolved
 *  selectItems — losing every alias for the outer SELECT. Swapping the
 *  wrapping `(`/`)` with single spaces preserves byte offsets while restoring
 *  the FROM list's expected shape. */
function unwrapJoinListParens(body: string): string {
  const re = /\b(from|join)\s*\(/gi;
  const chars = body.split('');
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    const openIdx = m.index + m[0].length - 1;
    const close = matchClosingParen(body, openIdx);
    if (close < 0) continue;
    const inner = body.slice(openIdx + 1, close);
    // Only unwrap when the inner content actually carries a JOIN — leaves
    // legitimate derived-table parens (`FROM (SELECT …) AS sub`) intact.
    if (!/\bjoin\b/i.test(inner)) continue;
    chars[openIdx] = ' ';
    chars[close] = ' ';
  }
  return chars.join('');
}

function matchClosingParen(body: string, openIdx: number): number {
  let depth = 1;
  for (let i = openIdx + 1; i < body.length; i++) {
    const c = body[i];
    if (c === '(') depth += 1;
    else if (c === ')') {
      depth -= 1;
      if (depth === 0) return i;
    }
    // Skip strings (basic; quoted identifiers handled by the parser later).
    else if (c === "'") {
      i = body.indexOf("'", i + 1);
      if (i < 0) return -1;
    }
  }
  return -1;
}
